/**
 * Persisted collapse/expand state for the app-shell Care Delivery AI sidebar.
 *
 * The chat now docks once at the app-shell level (it never unmounts on a tab
 * switch). It is collapsible and **defaults to collapsed** (BLA-313: the
 * editor is the primary object on this surface, and an empty chat panel was
 * taking ~25% of a full-screen laptop); the collapsed/expanded preference
 * persists to `localStorage` so it survives reloads once the provider makes an
 * explicit choice. When collapsed, the tab content reclaims the full width —
 * the chat's own hooks/state stay mounted (never unmount), so an in-progress
 * conversation is never lost, and the existing `Collapse Care Delivery AI`
 * control reopens it in one click. The preference is a non-PHI boolean.
 */
import { useCallback, useState } from "react";
import logger from "../utils/logger";

/** The localStorage key the collapse preference is persisted under. */
export const CHAT_COLLAPSE_KEY = "pcp.chat.collapsed";

/** The result surface returned by {@link useChatCollapse}. */
export interface UseChatCollapseResult {
  /** Whether the chat sidebar is collapsed (default true → collapsed). */
  collapsed: boolean;
  /** Toggle the collapsed/expanded state (persisted). */
  toggle: () => void;
}

/**
 * Read the persisted collapse preference, defaulting to collapsed.
 *
 * @returns False only when the provider has explicitly left the chat expanded
 *   on a prior visit; collapsed (true) when unset or when localStorage is
 *   unavailable.
 */
function readPreference(): boolean {
  try {
    const stored = window.localStorage.getItem(CHAT_COLLAPSE_KEY);
    return stored === null ? true : stored === "true";
  } catch {
    return true;
  }
}

/**
 * Persist the collapse preference, swallowing storage errors.
 *
 * @param collapsed - The new collapsed state to persist.
 */
function writePreference(collapsed: boolean): void {
  try {
    window.localStorage.setItem(CHAT_COLLAPSE_KEY, String(collapsed));
  } catch {
    logger.devOnly.warn("useChatCollapse: persist failed");
  }
}

/**
 * Manage the chat sidebar's collapse state with localStorage persistence.
 *
 * @returns The current collapsed flag and a toggle action.
 */
export function useChatCollapse(): UseChatCollapseResult {
  const [collapsed, setCollapsed] = useState<boolean>(readPreference);

  const toggle = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      writePreference(next);
      return next;
    });
  }, []);

  return { collapsed, toggle };
}
